/**
 * An import on the page (decision 40; part 18 log, table V): the file picked read and converted in part 17's sandbox,
 * the book it makes shown for review, and only on the writer's word its pictures added through part 16a's pipeline and
 * the book stored in one write (principle 5). Cancelling, or any refusal, stores nothing in the book.
 *
 * The sandbox, the pictures' importer and the workspace's commit are injected, so the logic runs in tests.
 */
import { imageDestination, settleImages } from '@cockatrice/core';
import type { ImportedBook } from '@cockatrice/core';
import { createSignal } from 'solid-js';
import type { Accessor } from 'solid-js';
import type { ImportResult } from '../images/importer.ts';
import type { ImportOutcome, ImportProblem, ImportRequest } from './host.ts';
import { importKind, nameTitle } from './kind.ts';
import type { PickedKind } from './kind.ts';
import type { ImportedFile } from './protocol.ts';

/** What the workspace's commit gives (V8, V10, V11). */
export type CommitResult = { readonly kind: 'created'; readonly chapters: number } | { readonly kind: 'not-editable' | 'save-failed' | 'stale' | 'failed' };

/** What the commit is asked to store: a new book, its ID chosen before its pictures were added (V9, V10). */
export interface CommitRequest {
  readonly bookId: string;
  readonly title: string;
  readonly subtitle: string | null;
  readonly chapters: readonly { readonly title: string | null; readonly text: string }[];
}

/** Why an import ended without a book: the sandbox's or worker's problem (V4), or the commit's (V8, V11), or K3. */
export type ImportFailure = ImportProblem | 'not-supported' | 'not-editable' | 'save-failed' | 'stale' | 'write-failed';

/** The book read, as the review shows it (V5). */
export interface Review {
  readonly fileName: string;
  readonly kind: PickedKind;
  /** The title the book gets unless the writer changes it (C5). */
  readonly title: string;
  readonly book: ImportedBook;
  readonly pictures: readonly ImportedFile[];
}

/** What the import shows (table V's outcomes). */
export type ImportState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'reading'; readonly fileName: string; readonly done: number; readonly total: number }
  | { readonly kind: 'review'; readonly review: Review }
  | { readonly kind: 'adding'; readonly done: number; readonly total: number }
  | { readonly kind: 'storing' }
  | { readonly kind: 'done'; readonly title: string; readonly chapters: number; readonly missing: number }
  | { readonly kind: 'failed'; readonly reason: ImportFailure };

/** What the controller needs; everything with an effect is injected. */
export interface ImportOptions {
  /** Part 17's sandbox (`ImportSandbox.run`). */
  readonly run: (file: Blob, format: PickedKind, request: ImportRequest) => Promise<ImportOutcome>;
  /** Part 16a's importer for one picture into book `bookId` (`importImage`, purpose `text`). */
  readonly addPicture: (bookId: string, file: Blob) => Promise<ImportResult>;
  /** The workspace's commit (`Workspace.importBook`). */
  readonly commit: (request: CommitRequest) => Promise<CommitResult>;
  /** A fresh book ID. */
  readonly newId: () => string;
}

/** The import's state and its one run. */
export class ImportController {
  readonly state: Accessor<ImportState>;
  readonly #setState: (state: ImportState) => void;
  readonly #options: ImportOptions;
  /** The import under way: a new number for each, so what an earlier one gives is ignored. */
  #job = 0;
  #abort: AbortController | null = null;

  constructor(options: ImportOptions) {
    this.#options = options;
    const [state, setState] = createSignal<ImportState>({ kind: 'idle' });
    this.state = state;
    this.#setState = setState;
  }

  /** Whether an import is reading, adding pictures or storing (V2: Import… is disabled then). */
  busy(): boolean {
    const kind = this.state().kind;
    return kind === 'reading' || kind === 'adding' || kind === 'storing';
  }

  /** V1–V5: reads `file`; its review follows, or why there is none. */
  pick(file: File): void {
    if (this.busy()) return;
    this.#job += 1;
    const job = this.#job;
    const kind = importKind(file.name);
    // K3: refused before a byte is read.
    if (kind === null) {
      this.#setState({ kind: 'failed', reason: 'not-supported' });
      return;
    }
    const abort = new AbortController();
    this.#abort = abort;
    this.#setState({ kind: 'reading', fileName: file.name, done: 0, total: file.size });
    void this.#options
      .run(file, kind, {
        signal: abort.signal,
        onProgress: (done, total) => {
          if (job === this.#job) this.#setState({ kind: 'reading', fileName: file.name, done, total });
        },
      })
      .then((outcome) => {
        if (job !== this.#job) return;
        this.#abort = null;
        if (outcome.kind === 'imported') this.#setState({ kind: 'review', review: review(file.name, kind, outcome.book, outcome.pictures) });
        // V3: a cancel ends in nothing to show; what a bundle holds is no book (K never sends one).
        else if (outcome.kind === 'refused' && outcome.problem === 'cancelled') this.#setState({ kind: 'idle' });
        else this.#setState({ kind: 'failed', reason: outcome.kind === 'refused' ? outcome.problem : 'failed' });
      });
  }

  /** V3, V9: stops the reading or the adding of pictures; nothing is stored. */
  cancel(): void {
    const kind = this.state().kind;
    if (kind !== 'reading' && kind !== 'adding') return;
    this.#abort?.abort();
    this.#abort = null;
    this.#job += 1;
    this.#setState({ kind: 'idle' });
  }

  /** V6, and putting a failure or an outcome away: back to nothing, the files dropped. */
  dismiss(): void {
    if (this.busy()) return;
    this.#job += 1;
    this.#setState({ kind: 'idle' });
  }

  /** V6: the review put away without importing (Cancel, Escape, the dialog closed); nothing else is touched. */
  closeReview(): void {
    if (this.state().kind !== 'review') return;
    this.#job += 1;
    this.#setState({ kind: 'idle' });
  }

  /** V8–V13: the reviewed book stored as a new book titled `title` (the file's when empty); settles when it ends. */
  confirm(title: string): Promise<void> {
    const state = this.state();
    if (state.kind !== 'review') return Promise.resolve();
    this.#job += 1;
    return this.#store(this.#job, state.review, title.trim() === '' ? state.review.title : title.trim());
  }

  async #store(job: number, reviewed: Review, title: string): Promise<void> {
    const { book, pictures } = reviewed;
    const bookId = this.#options.newId();
    const added = new Map<string, string>();
    let missing = 0;
    for (const [i, picture] of pictures.entries()) {
      this.#setState({ kind: 'adding', done: i, total: pictures.length });
      let result: ImportResult;
      try {
        result = await this.#options.addPicture(bookId, picture.file);
      } catch {
        result = { kind: 'failed' };
      }
      // V9: cancelled while a picture was added: nothing more.
      if (job !== this.#job) return;
      if (result.kind === 'added' || result.kind === 'existing') added.set(picture.key, imageDestination(result.reference));
      else missing += 1;
    }
    this.#setState({ kind: 'storing' });
    let result: CommitResult;
    try {
      result = await this.#options.commit({
        bookId,
        title,
        subtitle: book.subtitle,
        chapters: book.chapters.map((chapter) => ({ title: chapter.title, text: settleImages(chapter.text, added) })),
      });
    } catch {
      result = { kind: 'failed' };
    }
    if (result.kind === 'created') this.#setState({ kind: 'done', title, chapters: result.chapters, missing });
    else this.#setState({ kind: 'failed', reason: result.kind === 'failed' ? 'write-failed' : result.kind });
  }
}

/** V5: what the review shows of a book read from `fileName`. */
function review(fileName: string, kind: PickedKind, book: ImportedBook, pictures: readonly ImportedFile[]): Review {
  return {
    fileName,
    kind,
    title: book.title ?? nameTitle(fileName),
    book,
    pictures,
  };
}
